import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Check, Loader2, Calendar, FileText } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { tareasDespachoAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import ExpedienteSearch from '../ExpedienteSearch';
import { PRIORIDADES } from './constants';
import { formatDate } from './utils';

const TareaCostasTab = ({ tareas, loading, search, setSearch, filterCompletada, setFilterCompletada, filterPrioridad, setFilterPrioridad, loadTareas, handleDelete, handleToggleCompletada, usuarios, config }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [form, setForm] = useState({
    descripcion: '',
    observaciones: '',
    fecha_fin: '',
    asignado_a_id: '',
    asignado_a_nombre: '',
    prioridad: 'media'
  });

  const resetForm = () => {
    setForm({
      descripcion: '',
      observaciones: '',
      fecha_fin: '',
      asignado_a_id: '',
      asignado_a_nombre: '',
      prioridad: 'media'
    });
    setSelectedExpediente(null);
    setEditingTarea(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (tarea) => {
    setForm({
      descripcion: tarea.descripcion || '',
      observaciones: tarea.observaciones || '',
      fecha_fin: tarea.fecha_fin || '',
      asignado_a_id: tarea.asignado_a_id?.toString() || '',
      asignado_a_nombre: tarea.asignado_a_nombre || '',
      prioridad: tarea.prioridad || 'media'
    });
    setSelectedExpediente(tarea.expediente_id ? {
      id: tarea.expediente_id,
      numero_expediente: tarea.expediente_numero
    } : null);
    setEditingTarea(tarea);
    setIsDialogOpen(true);
    loadArchivosPastelitos(tarea.id);
  };

  const handleUsuarioChange = (userId) => {
    const user = usuarios.find(u => u.id.toString() === userId);
    setForm({
      ...form,
      asignado_a_id: userId,
      asignado_a_nombre: user?.nombre || user?.email || ''
    });
  };

  // Funciones para archivos
  const loadArchivosAbogados = async (tareaId) => {
    try {
      const res = await tareasDespachoAPI.abogados.getArchivos(tareaId);
      setArchivosAbogados(res.data || res || []);
    } catch (e) { setArchivosAbogados([]); }
  };

  const handleFileChangeAbogados = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleRemovePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteArchivoAbogados = async (archivoId) => {
    if (!confirm('¿Eliminar archivo?')) return;
    try {
      await tareasDespachoAPI.abogados.deleteArchivo(archivoId);
      toast({ title: 'Archivo eliminado' });
      if (editingTarea) loadArchivosAbogados(editingTarea.id);
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.descripcion) {
      toast({ title: 'Error', description: 'La descripción es obligatoria', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null,
        fecha_fin: form.fecha_fin || null
      };
      
      if (editingTarea) {
        await tareasDespachoAPI.costas.update(editingTarea.id, data);
        toast({ title: 'Tarea actualizada' });
      } else {
        await tareasDespachoAPI.costas.create(data);
        toast({ title: 'Tarea creada' });
      }
      if (editingTarea && pendingFiles.length > 0) {
        for (const file of pendingFiles) { await tareasDespachoAPI.pastelitos.uploadArchivo(editingTarea.id, file); }
        setPendingFiles([]);
      }
      setIsDialogOpen(false);
      resetForm();
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{config.nombre}</h2>
              <p className="text-sm text-gray-600">Costas procesales pendientes de pago</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar tareas..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadTareas()}
                />
              </div>
            </div>
            
            <Select value={filterCompletada} onValueChange={setFilterCompletada}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendientes">Pendientes</SelectItem>
                <SelectItem value="completadas">Completadas</SelectItem>
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA TAREA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : tareas.length > 0 ? (
            <div className="divide-y">
              {tareas.map((tarea) => (
                <div key={tarea.id} className={`p-4 hover:bg-gray-50 ${tarea.completada ? 'bg-gray-50 opacity-75' : ''}`}>
                  <div className="flex items-start gap-4">
                    <button 
                      onClick={() => handleToggleCompletada(tarea)}
                      className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        tarea.completada ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {tarea.completada && <Check className="w-4 h-4" />}
                    </button>
                    
                    <div className="flex-1">
                      <p className={`font-medium ${tarea.completada ? 'line-through text-gray-500' : ''}`}>
                        {tarea.descripcion}
                      </p>
                      {tarea.observaciones && (
                        <p className="text-sm text-gray-600 mt-1">{tarea.observaciones}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                        <Badge className={PRIORIDADES[tarea.prioridad]?.color}>
                          {PRIORIDADES[tarea.prioridad]?.nombre}
                        </Badge>
                        {tarea.expediente_numero && (
                          <a href={`/expedientes/${tarea.expediente_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline">
                            <FileText className="w-3 h-3" />{tarea.expediente_numero}
                          </a>
                        )}
                        {tarea.fecha_fin && (
                          <span className="flex items-center gap-1 text-gray-600">
                            <Calendar className="w-3 h-3" />Fin: {formatDate(tarea.fecha_fin)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleEdit(tarea)}>
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleDelete(tarea)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay tareas</p>
              <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
                <Plus className="w-4 h-4 mr-2" />Crear primera tarea
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              {editingTarea ? 'Editar Tarea' : 'Nueva Tarea Costas a Pagar'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Expediente relacionado</Label>
                <ExpedienteSearch
                  value={selectedExpediente}
                  onChange={setSelectedExpediente}
                  placeholder="Buscar expediente por número o título..."
                />
              </div>
              
              <div>
                <Label>Descripción *</Label>
                <Textarea 
                  value={form.descripcion}
                  onChange={(e) => setForm({...form, descripcion: e.target.value})}
                  placeholder="Descripción de la tarea..."
                  rows={2}
                  required
                />
              </div>
              
              <div>
                <Label>Observaciones</Label>
                <Textarea 
                  value={form.observaciones}
                  onChange={(e) => setForm({...form, observaciones: e.target.value})}
                  placeholder="Observaciones adicionales..."
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha fin</Label>
                  <Input 
                    type="date"
                    value={form.fecha_fin}
                    onChange={(e) => setForm({...form, fecha_fin: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            {editingTarea && (
              <div className="space-y-3 border-t pt-4 mt-4">
                <label className="text-sm font-medium">Archivos adjuntos</label>
                {archivosPastelitos.length > 0 && (<div className="space-y-2">{archivosPastelitos.map((a) => (<div key={a.id} className="flex items-center justify-between p-2 bg-gray-50 rounded"><a href={a.ruta} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm truncate">{a.nombre_original || a.nombre}</a><button type="button" onClick={() => handleDeleteArchivo(a.id)} className="text-red-500"><X className="w-4 h-4" /></button></div>))}</div>)}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); setPendingFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]); }}><input type="file" multiple onChange={handleFileChange} className="hidden" id="fileInputPastelitos" /><label htmlFor="fileInputPastelitos" className="cursor-pointer"><p className="text-gray-500">Arrastra archivos o <span className="text-blue-600">haz clic</span></p></label></div>
                {pendingFiles.length > 0 && (<div className="space-y-1">{pendingFiles.map((f, i) => (<div key={i} className="flex items-center justify-between text-sm bg-blue-50 p-2 rounded"><span>{f.name}</span><button type="button" onClick={() => handleRemoveFile(i)} className="text-red-500"><X className="w-4 h-4" /></button></div>))}</div>)}
              </div>
            )}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingTarea ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};


export default TareaCostasTab;
